import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, clearTokens, getToken, setTokens } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [consumer, setConsumer] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  const refreshProfile = useCallback(async () => {
    if (!getToken()) {
      setConsumer(null);
      return null;
    }
    try {
      const me = await apiGet("/consumer/profile");
      setConsumer(me);
      return me;
    } catch {
      setConsumer(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshProfile();
      setBootstrapped(true);
    })();
  }, [refreshProfile]);

  const login = useCallback(
    async (email, password) => {
      const data = await apiPost("/consumer/auth/login", { email, password }, { auth: false });
      setTokens(data.token, data.refreshToken);
      setConsumer(data.consumer);
      return data;
    },
    [],
  );

  const signup = useCallback(async (payload) => {
    const data = await apiPost("/consumer/auth/signup", payload, { auth: false });
    setTokens(data.token, data.refreshToken);
    setConsumer(data.consumer);
    return data;
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setConsumer(null);
    if (typeof window !== "undefined") window.location.href = "/";
  }, []);

  const value = useMemo(
    () => ({ consumer, setConsumer, bootstrapped, login, signup, logout, refreshProfile }),
    [consumer, bootstrapped, login, signup, logout, refreshProfile],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
